import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { STORE_EVALUATION_DETAIL_PAGE, STORE_QUALITY_DASHBOARD_PAGE } from "../routes";
import { listStoreEvaluationsApi } from "../storeEvaluationsApi";
import type { StoreEvaluation } from "../storeEvaluationsApi";
import { getMassBalanceStoresApi } from "../../estoque/api/massBalanceApi";
import type { StoreShort } from "../../estoque/api/massBalanceApi";
import { getUsersPaginatedApi } from "../../users/usersApi";
import type { UserListItem } from "../../users/usersApi";
import { listEvaluationFormsApi } from "../../evaluations/evaluationFormsApi";

import { useGoBack } from "../../../hooks/useGoBack";

import "./StoreEvaluationsAdminPage.css";

const PAGE_SIZE = 10;

/** Faixas de nota — as mesmas do painel de Qualidade. */
const scoreBand = (score01: number) => (score01 >= 0.8 ? "good" : score01 >= 0.5 ? "warn" : "bad");

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatShortDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const hasValue = (v: unknown) => v !== null && v !== undefined;

const StatusPill: React.FC<{ status: StoreEvaluation["status"] }> = ({ status }) =>
  status === "OPEN" ? (
    <span className="ecoPill evList__pill--open">Aberto</span>
  ) : (
    <span className="ecoPill">Finalizado</span>
  );

const ScoreCell: React.FC<{ ev: StoreEvaluation }> = ({ ev }) => {
  if (!hasValue(ev.score01)) return <span className="evList__noScore">Sem nota até finalizar</span>;
  const score = Number(ev.score01);
  return (
    <span className="evList__score">
      <span className="evList__bar" aria-hidden="true">
        <span
          className={`evList__barFill evList__barFill--${scoreBand(score)}`}
          style={{ width: `${Math.max(0, Math.min(1, score)) * 100}%` }}
        />
      </span>
      <strong>{(score * 100).toFixed(0)}%</strong>
      {hasValue(ev.stars) ? (
        <span className="evList__stars">
          {Number(ev.stars).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} de 5
        </span>
      ) : null}
    </span>
  );
};

export function StoreEvaluationsAdminPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  // Filters State
  const [stores, setStores] = useState<StoreShort[]>([]);
  const [supervisors, setSupervisors] = useState<UserListItem[]>([]);
  const [forms, setForms] = useState<any[]>([]);

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [selectedFormId, setSelectedFormId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [evaluations, setEvaluations] = useState<StoreEvaluation[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      if (!token) return;
      try {
        const [storesData, usersData, formsData] = await Promise.all([
          getMassBalanceStoresApi(token),
          getUsersPaginatedApi(1, 200),
          listEvaluationFormsApi({ category: "LOJA" })
        ]);
        setStores(storesData || []);

        // Filter users that are supervisors (SUPERVISOR, SUPERVISOR_DE_CAMPO)
        const supervisorUsers = (usersData.items || []).filter(
          (u) => u.userType === "SUPERVISOR" || u.userType === "SUPERVISOR_DE_CAMPO"
        );
        setSupervisors(supervisorUsers);
        setForms(formsData || []);
      } catch (err: any) {
        toast.error("Erro ao carregar opções de filtros.");
      }
    };
    loadFilters();
  }, [token]);

  const loadEvaluations = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const storesList = selectedStoreId ? [{ id: selectedStoreId }] : stores;

      if (storesList.length === 0 && !selectedStoreId) {
        setEvaluations([]);
        return;
      }

      const results = await Promise.all(
        storesList.map((store) =>
          listStoreEvaluationsApi({
            token,
            storeId: store.id,
            referenceFrom: startDate || undefined,
            referenceTo: endDate || undefined,
            answeredById: selectedSupervisorId || undefined,
            formId: selectedFormId || undefined,
            category: "LOJA",
            includeAnswers: false
          }).catch((err) => {
            console.error(`Erro ao buscar avaliações da loja ${store.id}`, err);
            return [];
          })
        )
      );

      let flattened = results.flat();

      if (statusFilter) {
        flattened = flattened.filter((ev) => ev.status === statusFilter);
      }

      // Sort desc
      flattened.sort((a, b) => new Date(b.answeredAt).getTime() - new Date(a.answeredAt).getTime());

      setEvaluations(flattened);
      setPage(1);
    } catch (err: any) {
      toast.error(err.message || "Erro ao carregar avaliações.");
    } finally {
      setLoading(false);
    }
  }, [token, stores, selectedStoreId, startDate, endDate, selectedSupervisorId, selectedFormId, statusFilter]);

  useEffect(() => {
    if (stores.length > 0) {
      loadEvaluations();
    }
  }, [stores, selectedStoreId, startDate, endDate, selectedSupervisorId, selectedFormId, statusFilter, loadEvaluations]);

  const handleClearFilters = () => {
    setSelectedStoreId("");
    setStartDate("");
    setEndDate("");
    setSelectedSupervisorId("");
    setSelectedFormId("");
    setStatusFilter("");
  };

  const openDetails = (id: string) => navigate(STORE_EVALUATION_DETAIL_PAGE(id));

  const activeFilterCount = [selectedStoreId, startDate, endDate, selectedSupervisorId, selectedFormId, statusFilter].filter(Boolean).length;
  const selectedStoreName = stores.find((s) => s.id === selectedStoreId)?.nomeFantasia;
  const statusLabel = statusFilter === "OPEN" ? "Abertas" : statusFilter === "SUBMITTED" ? "Finalizadas" : "Todos os status";
  const pageItems = evaluations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="evList">
      <main className="evList__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Avaliações de loja" },
          ]}
          title="Avaliações de loja"
          subtitle="Questionários de auditoria respondidos pelos supervisores em campo."
          onBack={goBack}
          backLabel="Voltar para Operações"
          actions={
            <button type="button" className="ecoBtn" onClick={() => navigate(STORE_QUALITY_DASHBOARD_PAGE())}>
              <EcoIcon name="barChart" size={18} />
              Ver painel de qualidade
            </button>
          }
        />

        <EcoFiltersCard
          activeCount={activeFilterCount}
          summary={`${selectedStoreName ?? "Todas as lojas"} · ${statusLabel}`}
        >
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="f-loja">Loja</label>
              <select id="f-loja" className="ecoSelect ecoSelect--sm" value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)}>
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeFantasia}
                  </option>
                ))}
              </select>
            </div>

            <div className="ecoField">
              <label htmlFor="f-sup">Supervisor</label>
              <select id="f-sup" className="ecoSelect ecoSelect--sm" value={selectedSupervisorId} onChange={(e) => setSelectedSupervisorId(e.target.value)}>
                <option value="">Todos</option>
                {supervisors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeCompleto}
                  </option>
                ))}
              </select>
            </div>

            <div className="ecoField">
              <label htmlFor="f-form">Formulário</label>
              <select id="f-form" className="ecoSelect ecoSelect--sm" value={selectedFormId} onChange={(e) => setSelectedFormId(e.target.value)}>
                <option value="">Todos</option>
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title || f.formType?.name || `Formulário - ${f.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="ecoField">
              <label htmlFor="f-status">Status</label>
              <select id="f-status" className="ecoSelect ecoSelect--sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Todos</option>
                <option value="OPEN">Aberto</option>
                <option value="SUBMITTED">Finalizado</option>
              </select>
            </div>

            <div className="ecoField">
              <label htmlFor="f-de">De</label>
              <input id="f-de" type="date" className="ecoInput ecoInput--sm" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
            </div>

            <div className="ecoField">
              <label htmlFor="f-ate">Até</label>
              <input id="f-ate" type="date" className="ecoInput ecoInput--sm" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <div className="ecoFilters__row">
            <button type="button" className="ecoBtn ecoFilters__clear" onClick={handleClearFilters}>
              Limpar
            </button>
          </div>
        </EcoFiltersCard>

        <section className="ecoCard evList__card" aria-label="Avaliações">
          <div className="evList__head">
            <span className="evList__count" aria-live="polite">
              {loading
                ? "Carregando avaliações..."
                : `${evaluations.length} ${evaluations.length === 1 ? "avaliação" : "avaliações"}`}
            </span>
            <span className="evList__sort">Ordenado por data, mais recente primeiro</span>
          </div>

          {!loading && evaluations.length === 0 ? (
            <div className="evList__empty">
              <p className="ecoEmpty">Nenhuma avaliação de loja encontrada com os filtros selecionados.</p>
            </div>
          ) : (
            <>
              {/* Desktop: tabela */}
              <div className="ecoTableWrap evList__table">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th scope="col">Loja</th>
                      <th scope="col">Formulário</th>
                      <th scope="col">Respondido em</th>
                      <th scope="col">Supervisor</th>
                      <th scope="col">Status</th>
                      <th scope="col">Nota</th>
                      <th scope="col" className="ecoTable__num">Ação</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading}>
                    {pageItems.map((ev) => (
                      <tr key={ev.id}>
                        <td className="evList__store">{ev.store?.nomeFantasia || "—"}</td>
                        <td className="ecoTable__muted">{ev.formType?.name || ev.form?.title || "Formulário"}</td>
                        <td className="ecoTable__muted evList__nowrap">{formatDateTime(ev.answeredAt)}</td>
                        <td className="ecoTable__muted">{ev.answeredBy?.nomeCompleto || "—"}</td>
                        <td><StatusPill status={ev.status} /></td>
                        <td><ScoreCell ev={ev} /></td>
                        <td className="ecoTable__num">
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--sm"
                            onClick={() => openDetails(ev.id)}
                            aria-label={`Ver detalhes da avaliação de ${ev.store?.nomeFantasia || "loja"}`}
                          >
                            Ver detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Celular: cards */}
              <ul className="evList__cards" aria-busy={loading}>
                {pageItems.map((ev) => (
                  <li key={ev.id}>
                    <button type="button" className="evList__cardItem" onClick={() => openDetails(ev.id)}>
                      <span className="evList__cardTop">
                        <span className="evList__cardText">
                          <span className="evList__cardStore">{ev.store?.nomeFantasia || "—"}</span>
                          <span className="evList__cardMeta">
                            {formatShortDateTime(ev.answeredAt)}
                            {ev.answeredBy?.nomeCompleto ? ` · ${ev.answeredBy.nomeCompleto}` : ""}
                          </span>
                        </span>
                        <StatusPill status={ev.status} />
                      </span>
                      <ScoreCell ev={ev} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="evList__pagerWrap">
            <EcoPagination page={page} pageSize={PAGE_SIZE} total={evaluations.length} onChange={setPage} disabled={loading} />
          </div>
        </section>
      </main>
    </div>
  );
}
